// Material icons import
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined'
import EventNoteIcon from '@mui/icons-material/EventNote'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import {
  AppBar,
  Avatar,
  AvatarProps,
  Box,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  TypographyProps,
} from '@mui/material'
import { MouseEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom' // Import i18n translation hook

import { useGetDriverProfile } from '../../../hooks/api/drivers/useGetDriverProfile'
import { useGetFileUrl } from '../../../hooks/useGetFileUrl'
import { useAuthContext } from '../../auth/hooks/useAuthContext'
import routes from '../../router/routes' // Import routes definition
import LanguageSelect from '../LanguageSelect' // Import language selector component
import NotificationMenu from '../NotificationMenu' // Notification Bar

const ENV_NAME = import.meta.env.VITE_ENVIRONMENT_NAME
const IS_DEV_OR_STAGING = ENV_NAME === 'staging' || ENV_NAME === 'development'

const TopAppBar = () => {
  const { signOut, userInfo, userStatus } = useAuthContext()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [anchorElUser, setAnchorElUser] = useState<HTMLElement | null>(null)
  const location = useLocation().pathname
  const { data: driverData, isLoading: fetchingDriver } = useGetDriverProfile()

  const profileAvatarUrl = useGetFileUrl(driverData?.profile_picture)

  const pages = [
    IS_DEV_OR_STAGING
      ? {
          disabled: userStatus?.status !== 'active', // value needs to be different from "active" to show section
          icon: <EventNoteIcon />,
          path: routes.portal.schedule,
          title: `${t('navigation.schedule')}`,
        }
      : undefined,
    {
      disabled: false,
      icon: <AttachMoneyOutlinedIcon />,
      path: routes.portal.earnings,
      title: `${t('navigation.earnings')}`,
    },
    {
      disabled: false,
      icon: <InsertDriveFileOutlinedIcon />,
      path: routes.portal.documents,
      title: `${t('navigation.documents')}`,
    },
    {
      disabled: false,
      icon: <SchoolOutlinedIcon />,
      path: routes.portal.training,
      title: `${t('navigation.training')}`,
    },
    {
      disabled: false,
      icon: <PersonOutlineOutlinedIcon />,
      path: routes.portal.profile,
      title: `${t('navigation.profile')}`,
    },
  ].filter((page): page is NonNullable<typeof page> => !!page)

  const handleCloseUserMenu = () => {
    setAnchorElUser(null)
  }

  const settings = [
    {
      action: () => {
        handleCloseUserMenu()
        navigate(routes.portal.profile)
      },
      title: `${t('usermenu.profile')}`,
    },
    {
      action: signOut,
      title: `${t('usermenu.logout')}`,
    },
  ]

  const textConfig: TypographyProps = {
    color: 'text.primary',
    textAlign: 'center',
    variant: 'body1',
  }

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget)
  }

  const stringToColor = (string: string) => {
    // Generate a random color from a string
    let hash = 0
    for (let i = 0; i < string.length; i += 1) {
      // eslint-disable-next-line no-bitwise
      hash = string.charCodeAt(i) + ((hash << 5) - hash)
    }
    let color = '#'
    for (let i = 0; i < 3; i += 1) {
      // eslint-disable-next-line no-bitwise
      const value = (hash >> (i * 8)) & 0xff
      color += `00${value.toString(16)}`.substr(-2)
    }
    return color
  }

  const avatarUrl = useMemo(
    () =>
      profileAvatarUrl
        ? `${import.meta.env.VITE_TRAINING_GUIDE_CLOUDFRONT_URL}${profileAvatarUrl}`
        : '',
    [profileAvatarUrl]
  )

  const avatarProps: AvatarProps = useMemo(() => {
    const name =
      userInfo?.firstName && userInfo?.lastName
        ? `${userInfo?.firstName} ${userInfo?.lastName}`
        : ''

    const initials = name
      .split(/\s/)
      .reduce((response, word) => response + word.slice(0, 1), '')

    return {
      children: initials,
      src: avatarUrl,
      sx: {
        backgroundColor: stringToColor(name),
      },
      variant: 'square',
    }
  }, [avatarUrl, userInfo?.firstName, userInfo?.lastName])

  return (
    <AppBar color="flat" position="static">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Open menu">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                {fetchingDriver ? (
                  <Avatar variant="square" />
                ) : (
                  <Avatar {...avatarProps} />
                )}
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorElUser}
              anchorOrigin={{
                horizontal: 'right',
                vertical: 'top',
              }}
              id="menu-appbar"
              keepMounted
              onClose={handleCloseUserMenu}
              open={Boolean(anchorElUser)}
              sx={{ mt: '45px' }}
              transformOrigin={{
                horizontal: 'right',
                vertical: 'top',
              }}
            >
              {settings.map((setting, index) => (
                <MenuItem key={index} onClick={setting.action}>
                  <Typography {...textConfig}>{setting.title}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <Typography
            component="div"
            noWrap
            sx={{
              display: { md: 'none', xs: 'flex' },
              flexGrow: 1,
              ml: 2,
              textTransform: 'capitalize',
            }}
            variant="h6"
          >
            {t(`navigation.${location.split('/').at(-1)}`)}
          </Typography>
          <Box
            sx={{
              display: { md: 'flex', xs: 'none' },
              flexGrow: 1,
              justifyContent: 'center',
            }}
          >
            {pages.map((page, idx) => (
              <Button
                color={location.includes(page.path) ? 'primary' : 'idle'}
                component={Link}
                disabled={page.disabled}
                key={idx}
                onClick={handleCloseUserMenu}
                startIcon={page.icon}
                sx={{
                  mx: 2,
                  my: 2,
                }}
                to={page.path}
                variant={location.includes(page.path) ? 'outlined' : 'text'}
              >
                {page.title}
              </Button>
            ))}
          </Box>

          <Box sx={{ flexGrow: 0, mr: 1 }}>
            {' '}
            {/* // language selector */}
            <LanguageSelect />
          </Box>

          <Box sx={{ flexGrow: 0 }}>
            {IS_DEV_OR_STAGING ? <NotificationMenu /> : null}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  )
}

export default TopAppBar
